import { useEffect, useState } from 'react';
import { Box, Card, Grid, FormControl } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InputText } from '@/components/shared/InputText';
import { useIsDirty } from '@/lib/hooks/useIsDirty';
import { FormActionButtons } from '@/components/shared/FormActionButtons';
import { CompanyDetailsResponse } from '@/models/payment';
import { approvalTypes } from '@/models/company';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { Dropdown } from '@/components/shared/Dropdown';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useCurrentCompanyDetails } from '@/services/paymentsQuery';
import { Title, SubTitle } from '@/typography/Typography';
import { ConnectPaymentService } from './common/ConnectPaymentService';
import styled from '@emotion/styled';
import { WithdrawalLimitsSettings } from '@/components/payments/WithdrawalLimitsSettings';
import {
    GridContainer,
    IntegrationCard,
    IntegrationCardPadding
} from './componants/Base';
import { LowBalanceSettings } from '@/components/payments/LowBalanceSettings';

export const ButtonContainer = styled(Box)`
    display: flex;
    justify-content: end;
    width: 100%;
    border-top: 1px solid #d0d5dd;
    padding: 24px;
`;

const ConnectedBankContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    marginTop: '16px',
    marginBottom: '16px',
    backgroundColor: '#F0F9F0',
    border: '1px solid #4CAF50',
    borderRadius: '8px',
    '& .bank-info': {
        marginLeft: '16px',
        flex: 1
    }
}));

const RemoveButton = styled('button')({
    backgroundColor: 'transparent',
    border: 'none',
    color: '#DC3545',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    textDecoration: 'underline',
    '&:hover': {
        textDecoration: 'none'
    }
});

export const PaymentSetting = () => {
    const { data: companyDetails, refetch } = useCurrentCompanyDetails();
    const { t } = useTranslation();
    const { updateCompanyDetails } = usePaymentsMutations();

    const { setAlert } = useNotificationsSnackbar();
    const [companyUpdateData, setCompanyUpdateData] =
        useState<CompanyDetailsResponse>({});

    // Track initial values for dirty state detection
    const [initialApprovalType, setInitialApprovalType] = useState<string>();
    const [initialTransactionEmployerText, setInitialTransactionEmployerText] =
        useState<string>();
    const [
        initialTransactionExecutionType,
        setInitialTransactionExecutionType
    ] = useState<string>();

    // Compute dirty states for each section (null / undefined / '' are treated as equivalent)
    const isApprovalTypeDirty = useIsDirty([
        {
            current: companyUpdateData?.approvalType,
            initial: initialApprovalType
        }
    ]);
    const isBankDetailsCompanyDirty = useIsDirty([
        {
            current: companyUpdateData?.transactionEmployerText,
            initial: initialTransactionEmployerText
        }
    ]);
    const isTransactionSpeedDirty = useIsDirty([
        {
            current: companyUpdateData?.transactionExecutionType,
            initial: initialTransactionExecutionType
        }
    ]);

    useEffect(() => {
        if (companyDetails?.data) {
            setCompanyUpdateData({
                approvalType: companyDetails.data.approvalType,
                transactionEmployerText:
                    companyDetails.data.transactionEmployerText,
                transactionExecutionType:
                    companyDetails.data.transactionExecutionType
            });

            setInitialApprovalType(companyDetails.data.approvalType);
            setInitialTransactionEmployerText(
                companyDetails.data.transactionEmployerText
            );
            setInitialTransactionExecutionType(
                companyDetails.data.transactionExecutionType
            );
        }
    }, [companyDetails]);

    const handlePayoutMethod = () => {
        updateCompanyDetails.mutate(companyUpdateData, {
            onSuccess: () =>
                setAlert('Payout method updated successfully.', 'success')
        });
    };

    const resetPaymentSetting = (sectionName: string) => {
        if (sectionName === 'transactionSpeed') {
            setCompanyUpdateData({
                ...companyUpdateData,
                transactionExecutionType: initialTransactionExecutionType
            });
        } else if (sectionName === 'bankDetailsCompany') {
            setCompanyUpdateData({
                ...companyUpdateData,
                transactionEmployerText: initialTransactionEmployerText
            });
        } else if (sectionName === 'paymentApprovalType') {
            setCompanyUpdateData({
                ...companyUpdateData,
                approvalType: initialApprovalType
            });
        }
    };

    return (
        <>
            <Grid container mt={2} justifyContent={'space-between'}>
                <Grid item xs={3}>
                    <Title>{t('payment_service')}</Title>
                    <SubTitle>{t('payment_service_description')}</SubTitle>
                </Grid>
                <Grid item xs={8.8}>
                    <IntegrationCard>
                        <IntegrationCardPadding>
                            <ConnectPaymentService
                                companyDetails={companyDetails}
                                reloadList={refetch}
                            />
                        </IntegrationCardPadding>
                    </IntegrationCard>
                </Grid>
            </Grid>
            {companyDetails?.data?.paymentProvider && (
                <Grid container mt={2} justifyContent={'space-between'}>
                    <Grid item xs={3}></Grid>
                    <Grid item xs={8.8}>
                        {companyDetails?.data?.paymentProvider === 'None' && (
                            <IntegrationCard sx={{ overflow: 'visible' }}>
                                <IntegrationCardPadding>
                                    <Title>{t('approval_type')}</Title>
                                    <FormControl fullWidth>
                                        <Dropdown
                                            label=""
                                            choices={approvalTypes?.map(
                                                (item) => ({
                                                    label: item,
                                                    value: item
                                                })
                                            )}
                                            value={
                                                companyUpdateData?.approvalType
                                            }
                                            onChange={(val: any) => {
                                                setCompanyUpdateData({
                                                    ...companyUpdateData,
                                                    approvalType: val
                                                });
                                            }}
                                            listContainerStyle={{
                                                fontWeight: 400
                                            }}
                                        />
                                    </FormControl>
                                    <SubTitle>
                                        {t('approval_type_note')}
                                    </SubTitle>

                                    <FormActionButtons
                                        show={isApprovalTypeDirty}
                                        onSave={handlePayoutMethod}
                                        onCancel={() =>
                                            resetPaymentSetting(
                                                'paymentApprovalType'
                                            )
                                        }
                                    />
                                </IntegrationCardPadding>
                            </IntegrationCard>
                        )}

                        {companyDetails?.data
                            ?.enableCustomTransactionMessages &&
                            (companyDetails?.data?.paymentProvider === 'Aiia' ||
                                companyDetails?.data?.paymentProvider ===
                                    'DanskeBank') && (
                                <Card>
                                    <GridContainer container>
                                        <InputText
                                            value={
                                                companyUpdateData?.transactionEmployerText
                                            }
                                            onChange={(val: any) => {
                                                setCompanyUpdateData({
                                                    ...companyUpdateData,
                                                    transactionEmployerText:
                                                        val.target.value ?? ''
                                                });
                                            }}
                                            label={t('bank_details_company')}
                                            placeholder="This is the placeholder default transaction text"
                                        />
                                        <SubTitle>
                                            {t('bank_details_company_note')}
                                        </SubTitle>
                                    </GridContainer>

                                    <FormActionButtons
                                        show={isBankDetailsCompanyDirty}
                                        onSave={handlePayoutMethod}
                                        onCancel={() =>
                                            resetPaymentSetting(
                                                'bankDetailsCompany'
                                            )
                                        }
                                    />
                                </Card>
                            )}
                    </Grid>
                </Grid>
            )}

            {companyDetails?.data?.paymentProvider === 'None' &&
                companyDetails?.data?.paymentSource === 'InstaPaidAccount' && (
                    <LowBalanceSettings currentCompany={companyDetails.data} />
                )}

            {companyDetails?.data?.paymentProvider === 'Aiia' && (
                <Grid
                    container
                    mt={3}
                    pb={3}
                    justifyContent={'space-between'}
                    borderTop={'1px solid #D0D5DD'}
                    pt={5}
                >
                    <Grid item xs={3}>
                        <Title>{t('transaction_type')}</Title>
                    </Grid>
                    <Grid item xs={8.8}>
                        <Card>
                            <GridContainer container>
                                <Title>{t('transaction_execution_type')}</Title>
                                <FormControl fullWidth>
                                    <Dropdown
                                        label=""
                                        choices={['Normal', 'Instant'].map(
                                            (item) => ({
                                                label: item,
                                                value: item
                                            })
                                        )}
                                        value={
                                            companyUpdateData?.transactionExecutionType
                                        }
                                        onChange={(val: any) => {
                                            setCompanyUpdateData({
                                                ...companyUpdateData,
                                                transactionExecutionType: val
                                            });
                                        }}
                                        listContainerStyle={{ fontWeight: 400 }}
                                    />
                                </FormControl>
                                <SubTitle>
                                    {t('transaction_execution_type_note')}
                                </SubTitle>
                            </GridContainer>
                            <FormActionButtons
                                show={isTransactionSpeedDirty}
                                onSave={handlePayoutMethod}
                                onCancel={() =>
                                    resetPaymentSetting('transactionSpeed')
                                }
                            />
                        </Card>
                    </Grid>
                </Grid>
            )}

            <WithdrawalLimitsSettings currentCompany={companyDetails?.data} />
        </>
    );
};
